import React, { useEffect, useState, Children, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon, XIcon, LockIcon, SparklesIcon, GiftIcon, ShoppingCartIcon } from 'lucide-react';
declare global {
  interface Window {
    fbq: (...args: unknown[]) => void;
  }
}
let pixelReady = false;
const firedEvents = new Set<string>();
const initMetaPixel = () => {
  if (typeof window === 'undefined') return;
  if (document.querySelector('script[src*="fbevents.js"]')) return;
  const f = window as any;
  if (f.fbq) return;
  const n = f.fbq = function (...args: any[]) {
    n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
  };
  if (!f._fbq) f._fbq = n;
  n.push = n;
  n.loaded = !0;
  n.version = '2.0';
  n.queue = [] as any[];
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  script.onload = () => {
    pixelReady = true;
  };
  document.head.appendChild(script);
  window.fbq('init', '1568815467533162');
};
const trackOnce = (eventName: string, params?: Record<string, unknown>) => {
  if (typeof window === 'undefined' || !window.fbq) return;
  if (firedEvents.has(eventName)) return;
  firedEvents.add(eventName);
  if (params) {
    window.fbq('track', eventName, params);
  } else {
    window.fbq('track', eventName);
  }
};
const trackPageView = () => {
  trackOnce('PageView');
};
const trackInitiateCheckout = (value: number) => {
  trackOnce('InitiateCheckout', {
    content_name: 'Couch Cash for Grandmas Store',
    content_category: 'Digital Product',
    value: value,
    currency: 'USD'
  });
};
function Modal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-2">
      {isOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-3">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} data-id="element-4" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.2
      }} className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" data-id="element-5">
            <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-6">
              <h2 className="text-2xl font-bold text-[#1E3A5F]" data-id="element-7">{title}</h2>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors" aria-label="Cerrar modal" data-id="element-8">
                <XIcon className="w-5 h-5 text-gray-500" data-id="element-9" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh] text-gray-700 leading-relaxed" data-id="element-10">
              {children}
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};
export function LandingPage() {
  const [selections, setSelections] = useState({
    vault: false,
    scout: false
  });
  const [justUnlocked, setJustUnlocked] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const isUnlocked = selections.vault && selections.scout;
  const totalPrice = (selections.vault ? 6.9 : 0) + (selections.scout ? 6.9 : 0);
  const hasSelection = selections.vault || selections.scout;
  useEffect(() => {
    initMetaPixel();
    const timer1 = setTimeout(() => trackPageView(), 500);
    const timer2 = setTimeout(() => trackPageView(), 2000);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);
  useEffect(() => {
    if (isUnlocked) {
      setJustUnlocked(true);
      const timer = setTimeout(() => setJustUnlocked(false), 3000);
      return () => clearTimeout(timer);
    } else {
      setJustUnlocked(false);
    }
  }, [isUnlocked]);
  const getCheckoutUrl = () => {
    if (selections.vault && selections.scout) {
      return 'https://www.mellowreset.com/cart/43083015389250:1';
    } else if (selections.vault) {
      return 'https://www.mellowreset.com/cart/43082982686786:1';
    } else if (selections.scout) {
      return 'https://www.mellowreset.com/cart/43082983473218:1';
    }
    return '#';
  };
  const handleCheckoutClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!hasSelection) {
      e.preventDefault();
      return;
    }
    trackInitiateCheckout(totalPrice);
  };
  return <div className="min-h-screen bg-[#FDF8F3] text-gray-800 flex flex-col font-sans" data-id="element-11">
      {/* Header */}
      <header className="bg-white border-b border-[#E8F0EB] py-3 md:py-6 px-4 sticky top-0 z-40 shadow-sm" data-id="element-12">
        <div className="max-w-5xl mx-auto text-center" data-id="element-13">
          <h1 className="text-lg md:text-3xl font-bold text-[#1E3A5F] mb-0 md:mb-1 font-serif" data-id="element-14">
            Cash Desde el Sofa Para Abuelas
          </h1>
          <p className="text-[#D4A853] font-medium text-xs md:text-base" data-id="element-15">
            Herramientas digitales para abuelas emprendedoras
          </p>
        </div>
      </header>

      <main className="flex-grow py-4 px-3 md:py-16 md:px-4" data-id="element-16">
        <div className="max-w-4xl mx-auto" data-id="element-17">
          {/* Motivational Prompt */}
          <div className="text-center mb-4 md:mb-10" data-id="element-18">
            <p className="font-bold text-xs md:text-base gold-pulse inline-block bg-[#1E3A5F] text-white px-4 py-2 md:px-6 md:py-3 rounded-full shadow-lg" data-id="element-19">
              ⬇️ Seleccione ambos y desbloquee la GUÍA GRATIS ⬇️
            </p>
          </div>

          {/* Product Grid */}
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6 mb-3 md:mb-8" data-id="element-20">
            {/* Product 1: Vault */}
            <motion.div variants={fadeInUp} onClick={() => setSelections(s => ({
            ...s,
            vault: !s.vault
          }))} className={`relative bg-white rounded-xl md:rounded-2xl p-3 md:p-6 cursor-pointer transition-all duration-300 border-2 flex flex-row md:flex-col items-center md:items-stretch gap-3 md:gap-0 h-auto md:h-full ${selections.vault ? 'border-[#D4A853] shadow-[0_4px_16px_rgba(212,168,83,0.2)] ring-1 ring-[#D4A853]' : 'border-transparent shadow-sm md:shadow-md hover:shadow-lg hover:border-gray-200'}`} data-id="element-21">
              <div className="absolute top-2 right-2 md:top-4 md:right-4 z-10" data-id="element-22">
                <div className={`w-5 h-5 md:w-6 md:h-6 rounded-full border-2 flex items-center justify-center transition-colors ${selections.vault ? 'bg-[#D4A853] border-[#D4A853]' : 'border-gray-300 bg-white'}`} data-id="element-23">
                  {selections.vault && <CheckIcon className="w-3 h-3 md:w-4 md:h-4 text-white" data-id="element-24" />}
                </div>
              </div>

              {/* Mobile: small thumbnail | Desktop: full image */}
              <div className="w-20 h-20 flex-shrink-0 md:w-full md:h-auto md:aspect-video md:mb-6 overflow-hidden rounded-lg md:rounded-xl bg-[#F5EDE4]" data-id="element-25">
                <img src="https://cdn.magicpatterns.com/uploads/s65S5hFFDu9CzrTgMavxd5/boveda_de_recetas.png" alt="La Lista de Recetas Clásicos de la Cocina" className="w-full h-full object-cover" data-id="element-26" />
              </div>

              <div className="flex-1 min-w-0 flex flex-col" data-id="element-27">
                <div className="hidden md:inline-flex items-center gap-1.5 bg-[#D4A853]/10 text-[#D4A853] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider self-start mb-4" data-id="element-28">
                  <SparklesIcon className="w-3.5 h-3.5" data-id="element-29" />
                  Muy Recomendado
                </div>
                <h3 className="text-sm md:text-xl font-bold text-[#1E3A5F] mb-0.5 md:mb-2 font-serif leading-tight pr-6 md:pr-0" data-id="element-30">
                  La Lista de Recetas "Clásicos de la Cocina"
                </h3>
                <p className="text-base md:text-2xl font-bold text-[#D4A853] mb-0 md:mb-4" data-id="element-31">
                  $6.90
                </p>
                <p className="hidden md:block text-gray-600 text-sm leading-relaxed flex-grow" data-id="element-32">
                  Una Lista para copiar y pegar de 20 recetas de alimentos
                  integrales (10 Aptas para Diabéticos + 10 Favoritas de la
                  Familia). Ya hecho para usted: pase de la "Idea" al "Producto
                  Terminado" hoy mismo.
                </p>
                <button className={`hidden md:block mt-6 w-full py-3 rounded-xl font-bold transition-colors ${selections.vault ? 'bg-[#D4A853]/10 text-[#D4A853] border border-[#D4A853]/30' : 'bg-[#1E3A5F] text-white hover:bg-[#2a4d7a]'}`} data-id="element-33">
                  {selections.vault ? 'Seleccionado' : 'Agregar al pedido'}
                </button>
              </div>
            </motion.div>

            {/* Product 2: Scout */}
            <motion.div variants={fadeInUp} onClick={() => setSelections(s => ({
            ...s,
            scout: !s.scout
          }))} className={`relative bg-white rounded-xl md:rounded-2xl p-3 md:p-6 cursor-pointer transition-all duration-300 border-2 flex flex-row md:flex-col items-center md:items-stretch gap-3 md:gap-0 h-auto md:h-full ${selections.scout ? 'border-[#D4A853] shadow-[0_4px_16px_rgba(212,168,83,0.2)] ring-1 ring-[#D4A853]' : 'border-transparent shadow-sm md:shadow-md hover:shadow-lg hover:border-gray-200'}`} data-id="element-34">
              <div className="absolute top-2 right-2 md:top-4 md:right-4 z-10" data-id="element-35">
                <div className={`w-5 h-5 md:w-6 md:h-6 rounded-full border-2 flex items-center justify-center transition-colors ${selections.scout ? 'bg-[#D4A853] border-[#D4A853]' : 'border-gray-300 bg-white'}`} data-id="element-36">
                  {selections.scout && <CheckIcon className="w-3 h-3 md:w-4 md:h-4 text-white" data-id="element-37" />}
                </div>
              </div>

              {/* Mobile: small thumbnail | Desktop: full image */}
              <div className="w-20 h-20 flex-shrink-0 md:w-full md:h-auto md:aspect-video md:mb-6 overflow-hidden rounded-lg md:rounded-xl bg-[#F5EDE4]" data-id="element-38">
                <img src="https://cdn.magicpatterns.com/uploads/7fKArh88prD5o8usSh2uSM/el_sistema.png" alt="El Sistema Explorador de Compradores" className="w-full h-full object-cover" data-id="element-39" />
              </div>

              <div className="flex-1 min-w-0 flex flex-col" data-id="element-40">
                <div className="hidden md:inline-flex items-center gap-1.5 bg-[#D4A853]/10 text-[#D4A853] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider self-start mb-4" data-id="element-41">
                  <SparklesIcon className="w-3.5 h-3.5" data-id="element-42" />
                  Muy Recomendado
                </div>
                <h3 className="text-sm md:text-xl font-bold text-[#1E3A5F] mb-0.5 md:mb-2 font-serif leading-tight pr-6 md:pr-0" data-id="element-43">
                  El Sistema "Explorador de Compradores"
                </h3>
                <p className="text-base md:text-2xl font-bold text-[#D4A853] mb-0 md:mb-4" data-id="element-44">
                  $6.90
                </p>
                <p className="hidden md:block text-gray-600 text-sm leading-relaxed flex-grow" data-id="element-45">
                  Encuentre grupos "ocultos" de Facebook donde pasan el rato más
                  de 500,000 amantes de las recetas. Incluye el Guion Mágico de
                  2 Oraciones para que los clientes pidan su enlace de Etsy. Sin
                  anuncios, sin dolores de cabeza técnicos.
                </p>
                <button className={`hidden md:block mt-6 w-full py-3 rounded-xl font-bold transition-colors ${selections.scout ? 'bg-[#D4A853]/10 text-[#D4A853] border border-[#D4A853]/30' : 'bg-[#1E3A5F] text-white hover:bg-[#2a4d7a]'}`} data-id="element-46">
                  {selections.scout ? 'Seleccionado' : 'Agregar al pedido'}
                </button>
              </div>
            </motion.div>
          </motion.div>

          {/* Locked/Unlocked Product 3 */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3,
          duration: 0.5
        }} className="mb-4 md:mb-12" data-id="element-47">
            <AnimatePresence mode="wait" data-id="element-48">
              {isUnlocked ? <motion.div key="unlocked" initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.95
            }} className={`relative bg-white rounded-xl md:rounded-2xl border-2 border-[#D4A853] shadow-[0_0_25px_rgba(212,168,83,0.4)] overflow-hidden ${justUnlocked ? 'unlock-shimmer' : ''}`} data-id="element-49">
                  <AnimatePresence data-id="element-50">
                    {justUnlocked && <motion.div initial={{
                  y: -40,
                  opacity: 0
                }} animate={{
                  y: 0,
                  opacity: 1
                }} exit={{
                  y: -40,
                  opacity: 0
                }} className="absolute top-0 left-0 right-0 z-20 bg-[#D4A853] text-black font-bold text-center py-1.5 md:py-2 text-xs md:text-sm shadow-md flex items-center justify-center gap-2 celebration-pop" data-id="element-51">
                        <SparklesIcon className="w-3 h-3 md:w-4 md:h-4" data-id="element-52" />
                        🎉 ¡GUÍA EXCLUSIVA DESBLOQUEADA!
                      </motion.div>}
                  </AnimatePresence>

                  <div className="p-3 md:p-8 flex flex-row gap-3 md:gap-6 items-center md:items-start mt-6 md:mt-0" data-id="element-53">
                    <div className="w-20 h-20 md:w-1/3 md:h-48 flex-shrink-0 rounded-lg md:rounded-xl overflow-hidden shadow-md relative" data-id="element-54">
                      <div className="absolute top-1 left-1 md:top-2 md:left-2 bg-green-500 text-white text-[10px] md:text-xs font-bold px-1.5 py-0.5 md:px-2 md:py-1 rounded-md z-10 flex items-center gap-0.5 md:gap-1 shadow-sm" data-id="element-55">
                        <CheckIcon className="w-2.5 h-2.5 md:w-3 md:h-3" data-id="element-56" />
                        <span className="hidden md:inline" data-id="element-57">DESBLOQUEADO</span>
                        <span className="md:hidden" data-id="element-58">✓</span>
                      </div>
                      <img src="https://cdn.magicpatterns.com/uploads/sgsDQpKFfBYPbHGtQj9vpq/tienda_de_etsy.png" alt="El Mapa de Tienda de Etsy Sin Adivinanzas" className="w-full h-full object-cover" data-id="element-59" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-60">
                      <div className="hidden md:flex items-center gap-2 mb-2" data-id="element-61">
                        <GiftIcon className="w-5 h-5 text-[#D4A853]" data-id="element-62" />
                        <span className="text-[#D4A853] font-bold text-sm tracking-widest uppercase" data-id="element-63">
                          Regalo Exclusivo
                        </span>
                      </div>
                      <h3 className="text-sm md:text-2xl font-bold text-[#1E3A5F] mb-0.5 md:mb-2 font-serif leading-tight" data-id="element-64">
                        El Mapa de Tienda de Etsy "Sin Adivinanzas"
                      </h3>
                      <div className="flex items-center gap-2 mb-1 md:mb-4" data-id="element-65">
                        <span className="text-gray-400 line-through text-sm md:text-lg" data-id="element-66">
                          $9.00
                        </span>
                        <span className="text-green-500 font-bold text-base md:text-xl" data-id="element-67">
                          GRATIS
                        </span>
                      </div>
                      <p className="hidden md:block text-gray-600 text-sm md:text-base leading-relaxed" data-id="element-68">
                        Configure su tienda de Etsy con cero "estrés
                        tecnológico". Siga este mapa visual para ver exactamente
                        qué botones presionar para una configuración 100%
                        correcta de su tienda, impuestos e información bancaria
                        la primera vez.
                      </p>
                    </div>
                  </div>
                </motion.div> : <motion.div key="locked" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} className="bg-white/60 rounded-xl md:rounded-2xl border-2 border-dashed border-gray-300 overflow-hidden opacity-80 order-bump-flash" data-id="element-69">
                  <div className="bg-gray-100 px-3 py-2 md:px-4 md:py-3 border-b border-gray-200 flex items-center justify-center gap-2 text-gray-500 font-medium text-xs md:text-sm" data-id="element-70">
                    <LockIcon className="w-3.5 h-3.5 md:w-4 md:h-4" data-id="element-71" />
                    Seleccione ambos para desbloquear GRATIS
                  </div>
                  <div className="p-3 md:p-8 flex flex-row gap-3 md:gap-6 items-center md:items-start" data-id="element-72">
                    <div className="w-20 h-20 md:w-1/3 md:h-48 flex-shrink-0 rounded-lg md:rounded-xl overflow-hidden bg-gray-200 relative" data-id="element-73">
                      <div className="absolute inset-0 bg-gray-900/10 backdrop-grayscale flex items-center justify-center z-10" data-id="element-74">
                        <LockIcon className="w-8 h-8 md:w-12 md:h-12 text-gray-400 opacity-50" data-id="element-75" />
                      </div>
                      <img src="https://cdn.magicpatterns.com/uploads/sgsDQpKFfBYPbHGtQj9vpq/tienda_de_etsy.png" alt="Locked Product" className="w-full h-full object-cover grayscale opacity-40" data-id="element-76" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-77">
                      <h3 className="text-sm md:text-2xl font-bold text-gray-400 mb-0.5 md:mb-2 font-serif leading-tight" data-id="element-78">
                        El Mapa de Tienda de Etsy "Sin Adivinanzas"
                      </h3>
                      <div className="flex items-center gap-2 mb-1 md:mb-4" data-id="element-79">
                        <span className="text-gray-400 line-through text-sm md:text-lg" data-id="element-80">
                          $9.00
                        </span>
                        <span className="text-gray-400 font-bold text-base md:text-xl" data-id="element-81">
                          GRATIS
                        </span>
                      </div>
                      <p className="hidden md:block text-gray-400 text-sm md:text-base leading-relaxed" data-id="element-82">
                        Configure su tienda de Etsy con cero "estrés
                        tecnológico". Siga este mapa visual para ver exactamente
                        qué botones presionar para una configuración 100%
                        correcta de su tienda, impuestos e información bancaria
                        la primera vez.
                      </p>
                    </div>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </motion.div>

          {/* Order Summary */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4,
          duration: 0.5
        }} className="bg-[#1E3A5F] text-white rounded-2xl md:rounded-3xl p-4 md:p-10 shadow-2xl relative overflow-hidden" data-id="element-83">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none" data-id="element-84"></div>

            <h2 className="text-lg md:text-2xl font-bold mb-3 md:mb-6 font-serif flex items-center gap-2 md:gap-3" data-id="element-85">
              <ShoppingCartIcon className="w-5 h-5 md:w-6 md:h-6 text-[#D4A853]" data-id="element-86" />
              Resumen de su pedido
            </h2>

            <div className="space-y-2 md:space-y-4 mb-4 md:mb-8" data-id="element-87">
              {!hasSelection ? <p className="text-gray-400 italic text-center py-2 md:py-4 text-sm" data-id="element-88">
                  Su carrito está vacío. Seleccione un producto arriba.
                </p> : <>
                  {selections.vault && <div className="flex justify-between items-center border-b border-white/10 pb-2 md:pb-4" data-id="element-89">
                      <span className="text-gray-200 text-sm md:text-base" data-id="element-90">
                        La Lista de Recetas "Clásicos..."
                      </span>
                      <span className="font-bold text-[#D4A853] text-sm md:text-base" data-id="element-91">
                        $6.90
                      </span>
                    </div>}
                  {selections.scout && <div className="flex justify-between items-center border-b border-white/10 pb-2 md:pb-4" data-id="element-92">
                      <span className="text-gray-200 text-sm md:text-base" data-id="element-93">
                        El Sistema "Explorador..."
                      </span>
                      <span className="font-bold text-[#D4A853] text-sm md:text-base" data-id="element-94">
                        $6.90
                      </span>
                    </div>}
                  {isUnlocked && <div className="flex justify-between items-center border-b border-white/10 pb-2 md:pb-4" data-id="element-95">
                      <span className="text-green-400 flex items-center gap-1.5 md:gap-2 text-sm md:text-base" data-id="element-96">
                        <GiftIcon className="w-3.5 h-3.5 md:w-4 md:h-4" data-id="element-97" />
                        El Mapa de Tienda de Etsy
                      </span>
                      <span className="font-bold text-green-400 text-sm md:text-base" data-id="element-98">
                        GRATIS
                      </span>
                    </div>}
                </>}
            </div>

            <div className="flex items-end justify-between mb-4 md:mb-8 pt-1 md:pt-2" data-id="element-99">
              <div data-id="element-100">
                <p className="text-gray-400 text-xs md:text-sm mb-1" data-id="element-101">
                  Total a pagar hoy
                </p>
                {isUnlocked && <p className="text-green-400 text-[10px] md:text-xs font-medium bg-green-400/10 px-1.5 py-0.5 md:px-2 md:py-1 rounded inline-block" data-id="element-102">
                    Ha ahorrado $9.00
                  </p>}
              </div>
              <div className="text-right" data-id="element-103">
                <span className="text-3xl md:text-5xl font-bold text-white" data-id="element-104">
                  ${totalPrice.toFixed(2)}
                </span>
              </div>
            </div>

            <a href={getCheckoutUrl()} onClick={handleCheckoutClick} className={`block w-full py-3 md:py-4 px-6 md:px-8 rounded-xl text-center font-bold text-base md:text-lg transition-all transform ${hasSelection ? 'bg-[#D4A853] hover:bg-[#c29642] text-[#1E3A5F] shadow-lg hover:-translate-y-1 hover:shadow-xl cursor-pointer' : 'bg-gray-600 text-gray-400 cursor-not-allowed'}`} data-id="element-105">
              {hasSelection ? 'Completar Compra Segura' : 'Seleccione al menos un producto'}
            </a>

            <div className="mt-2 md:mt-4 text-center" data-id="element-106">
              <p className="text-[10px] md:text-xs text-gray-400 flex items-center justify-center gap-1" data-id="element-107">
                <LockIcon className="w-3 h-3" data-id="element-108" /> Pago 100% seguro y encriptado
              </p>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-4 md:py-8 px-4 mt-auto" data-id="element-109">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-110">
          <p className="text-gray-500 text-sm" data-id="element-111">
            © {new Date().getFullYear()} Couch Cash for Grandmas. Todos los
            derechos reservados.
          </p>
          <div className="flex items-center gap-6" data-id="element-112">
            <button onClick={() => setPrivacyOpen(true)} className="text-gray-500 hover:text-[#1E3A5F] text-sm transition-colors" data-id="element-113">
              Política de Privacidad
            </button>
            <button onClick={() => setTermsOpen(true)} className="text-gray-500 hover:text-[#1E3A5F] text-sm transition-colors" data-id="element-114">
              Términos de Servicio
            </button>
          </div>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      <Modal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Política de Privacidad" data-id="element-115">
        <div className="space-y-4" data-id="element-116">
          <p className="text-sm text-gray-500" data-id="element-117">
            Última actualización:{' '}
            {new Date().toLocaleDateString('es', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })}
          </p>
          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-118">
            Información que Recopilamos
          </h3>
          <p data-id="element-119">
            Cuando compra nuestros productos digitales, recopilamos solo la
            información necesaria para procesar su pedido, incluyendo su
            dirección de correo electrónico e información de pago. El
            procesamiento de pagos es manejado de forma segura por proveedores
            externos.
          </p>
          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-120">
            Cómo Usamos Su Información
          </h3>
          <p data-id="element-121">
            Usamos su información únicamente para entregar los productos
            digitales comprados y para enviar actualizaciones importantes sobre
            su compra. No vendemos, alquilamos ni compartimos su información
            personal con terceros con fines de marketing.
          </p>
          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-122">
            Entrega de Productos Digitales
          </h3>
          <p data-id="element-123">
            Nuestros productos se entregan digitalmente. Una vez completada su
            compra, recibirá acceso para descargar sus archivos. No se envían
            productos físicos.
          </p>
          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-124">Cookies</h3>
          <p data-id="element-125">
            Nuestro sitio web puede usar cookies para mejorar su experiencia de
            navegación. Puede optar por desactivar las cookies en la
            configuración de su navegador.
          </p>
          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-126">Sus Derechos</h3>
          <p data-id="element-127">
            Tiene derecho a acceder, corregir o eliminar su información
            personal. También puede optar por no recibir comunicaciones de
            marketing en cualquier momento.
          </p>
          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-128">
            Cambios a Esta Política
          </h3>
          <p data-id="element-129">
            Podemos actualizar esta política de privacidad de vez en cuando.
            Cualquier cambio se publicará en esta página con una fecha de
            revisión actualizada.
          </p>
        </div>
      </Modal>

      {/* Terms of Service Modal */}
      <Modal isOpen={termsOpen} onClose={() => setTermsOpen(false)} title="Términos de Servicio" data-id="element-130">
        <div className="space-y-4" data-id="element-131">
          <p className="text-sm text-gray-500" data-id="element-132">
            Última actualización:{' '}
            {new Date().toLocaleDateString('es', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })}
          </p>
          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-133">
            Compra de Producto Digital
          </h3>
          <p data-id="element-134">
            Al comprar "Couch Cash for Grandmas", está comprando una guía en
            formato de libro electrónico digital. Este es un producto educativo
            diseñado para ayudarle a aprender cómo crear y vender tarjetas de
            recetas imprimibles.
          </p>
          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-135">
            Lo Que Está Obteniendo
          </h3>
          <p data-id="element-136">
            Este producto es una guía instructiva paso a paso. No incluye
            tarjetas de recetas prefabricadas, plantillas para reventa ni ningún
            producto físico. Los resultados dependen de su propio esfuerzo e
            implementación.
          </p>
          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-137">Sin Garantías</h3>
          <p data-id="element-138">
            Aunque proporcionamos orientación práctica, no podemos garantizar
            ningún ingreso o resultado específico. El éxito depende de muchos
            factores, incluyendo su esfuerzo, las condiciones del mercado y la
            ejecución.
          </p>
          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-139">
            Política de Reembolso
          </h3>
          <p data-id="element-140">
            Debido a la naturaleza digital de este producto, todas las ventas
            son finales. Una vez que reciba acceso para descargar el libro
            electrónico, no hay reembolsos disponibles. Por favor, lea la
            descripción del producto cuidadosamente antes de comprar.
          </p>
          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-141">
            Propiedad Intelectual
          </h3>
          <p data-id="element-142">
            Este libro electrónico es solo para su uso personal. No puede
            revender, redistribuir ni compartir el contenido con otros. El
            contenido está protegido por derechos de autor.
          </p>
          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-143">
            Limitación de Responsabilidad
          </h3>
          <p data-id="element-144">
            No somos responsables de ningún daño o pérdida que resulte de su uso
            de este producto o de sus actividades comerciales. Esta guía es solo
            para fines educativos.
          </p>
          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-145">Acuerdo</h3>
          <p data-id="element-146">
            Al comprar este producto, usted acepta estos términos de servicio.
          </p>
        </div>
      </Modal>
    </div>;
}